// The places both UIs have, by the route that draws each (spec ui-perf/ui-switch-classic-simple-pro,
// "The same place"): the pill opens the same place in the other UI, else that UI's home.

/** The three UIs a person can choose (the `ui` preference). */
export type Ui = "classic" | "simple" | "pro";

/** A place Classic and Pro both have; the staff pages are one in all three UIs. */
export type Place =
  | "home"
  | "image"
  | "video"
  | "sound"
  | "actors"
  | "generations"
  | "templates"
  | "library"
  | "products"
  | "brands"
  | "settings"
  | "account"
  | "appearance"
  | "achievements"
  | "tokens"
  | "newTeam"
  | "editor"
  | "staff";

const TEAM = "/signed-in/teams/$teamId";
const STAFF = "/signed-in/staff";
const BRAND = `${TEAM}/brands/$brandId`;
const PRO = `${BRAND}/studio/pro`;
const SIMPLE = `${BRAND}/studio/simple`;

/** Classic's routes, each by the place it is; a page's own pages (a product, a job) count as it. */
const CLASSIC: Record<string, Place> = {
  [`${TEAM}/`]: "home",
  [`${BRAND}/generate`]: "image",
  [`${BRAND}/sounds`]: "sound",
  [`${BRAND}/actors`]: "actors",
  [`${TEAM}/jobs`]: "generations",
  [`${TEAM}/jobs/$jobId`]: "generations",
  [`${BRAND}/templates`]: "templates",
  [`${BRAND}/library`]: "library",
  [`${BRAND}/products`]: "products",
  [`${BRAND}/products/new`]: "products",
  [`${BRAND}/products/$productId`]: "products",
  [`${TEAM}/brands`]: "brands",
  [`${TEAM}/brands/new`]: "brands",
  [`${BRAND}/`]: "brands",
  [`${BRAND}/edit`]: "brands",
  [`${TEAM}/members`]: "settings",
  [`${TEAM}/account`]: "account",
  [`${TEAM}/account/appearance`]: "appearance",
  [`${TEAM}/account/achievements`]: "achievements",
  [`${TEAM}/tokens`]: "tokens",
  "/signed-in/teams/new": "newTeam",
  [`${BRAND}/editor`]: "editor",
};

/** Pro's routes, each by the place it is; Talking head is a tool of Classic's Video page. */
const PRO_PLACES: Record<string, Place> = {
  [`${PRO}/`]: "home",
  [`${PRO}/create-image`]: "image",
  [`${PRO}/create-video`]: "video",
  [`${PRO}/create-talking`]: "video",
  [`${PRO}/create-sound`]: "sound",
  [`${PRO}/actors`]: "actors",
  [`${PRO}/generations`]: "generations",
  [`${PRO}/templates`]: "templates",
  [`${PRO}/library`]: "library",
  [`${PRO}/library/files`]: "library",
  [`${PRO}/products`]: "products",
  [`${PRO}/products/new`]: "products",
  [`${PRO}/products/$productId`]: "products",
  [`${PRO}/brands`]: "brands",
  [`${PRO}/brands/new`]: "brands",
  [`${PRO}/brand`]: "brands",
  [`${PRO}/brand/edit`]: "brands",
  [`${PRO}/settings`]: "settings",
  [`${PRO}/settings/account`]: "account",
  [`${PRO}/settings/appearance`]: "appearance",
  [`${PRO}/settings/achievements`]: "achievements",
  [`${PRO}/settings/tokens`]: "tokens",
  [`${PRO}/sounds`]: "sound",
  [`${PRO}/teams/new`]: "newTeam",
  [`${PRO}/editor`]: "editor",
};

/**
 * The UI a route draws, and the place it is in it, if both UIs have it. A staff page is drawn in
 * the UI its `?ui=` names, `frame` (spec ui-perf/admin-panel-pro-simple).
 */
export function whereIs(
  routeId: string,
  medium: string | undefined,
  frame: Ui = "classic",
): { ui: Ui; place: Place | undefined } {
  // Simple's root and every page under it, never a sibling that only starts with its name.
  if (routeId === SIMPLE || routeId.startsWith(`${SIMPLE}/`)) return { ui: "simple", place: undefined };
  if (routeId.startsWith(PRO) || routeId === `${TEAM}/pro`) {
    return { ui: "pro", place: PRO_PLACES[routeId] };
  }
  if (routeId === STAFF || routeId.startsWith(`${STAFF}/`)) return { ui: frame, place: "staff" };
  const place = CLASSIC[routeId];
  // Classic's one Studio page is Image or Video by its `?medium=`.
  return { ui: "classic", place: place === "image" && medium === "video" ? "video" : place };
}
